import { useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { Globe } from "./Globe";
import { PopulationClusters } from "./PopulationClusters";
import { sunParams } from "../lib/lighting";
import { latLonToVector3 } from "../lib/seismic";
import { useSim } from "../lib/store";

const INTRO_DUR = 7; // seconds
const CY = 0.25; // globe centre Y — lowered to give breathing room below the top bar

function runningMaxMag(st) {
  if (!st.simulating) return 0;
  const elapsed = (performance.now() - st.simStart) / 1000;
  let m = 0;
  for (const e of st.events) if (elapsed <= e.duration + 0.5) m = Math.max(m, e.magnitude);
  return m;
}

export function MacroScene() {
  const { camera } = useThree();
  const floatRef = useRef();
  const controlsRef = useRef();
  const ambientRef = useRef();
  const sunRef = useRef();
  const introStartRef = useRef(null);
  const macroFlyRef = useRef(null);
  const lastMacroNonceRef = useRef(0);
  const setCanEnterStreet = useSim((s) => s.setCanEnterStreet);
  const simulating = useSim((s) => s.simulating);
  const clickStage = useSim((s) => s.clickStage);
  const locked = clickStage >= 1 && !simulating;

  useFrame((state) => {
    const st = useSim.getState();

    // --- Guided Tour: smoothly orbit to frame each famous-quake epicenter ---
    if (st.tourActive && st.tourFocus) {
      if (controlsRef.current) controlsRef.current.enabled = false;
      const target = new THREE.Vector3(0, CY, 0);
      const dir = latLonToVector3(st.tourFocus.lat, st.tourFocus.lon, 1).normalize();
      const desired = target.clone().add(dir.multiplyScalar(4.4));
      camera.position.lerp(desired, 0.035);
      camera.lookAt(target);
      // still animate the levitation shake so tour ruptures feel alive
      if (floatRef.current) {
        const mag = runningMaxMag(st);
        const amp = mag > 0 ? 0.008 + Math.pow(Math.max(0, (mag - 3) / 6.5), 2.4) * 0.09 : 0;
        const tt = state.clock.elapsedTime;
        floatRef.current.position.x = Math.sin(tt * 34) * amp;
        floatRef.current.position.y = CY + Math.sin(tt * 0.9) * 0.05 + Math.cos(tt * 29) * amp * 0.7;
      }
      return;
    }

    // --- Cinematic intro fly-around ---
    if (st.introPlaying) {
      if (introStartRef.current == null) introStartRef.current = performance.now();
      const t = (performance.now() - introStartRef.current) / 1000;
      const p = Math.min(t / INTRO_DUR, 1);
      const ease = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      const ang = ease * Math.PI * 1.5 - Math.PI / 2;
      const dist = 11.5 - ease * 4.0;
      const height = 3.6 - ease * 2.6;
      camera.position.set(Math.cos(ang) * dist, CY + height, Math.sin(ang) * dist);
      camera.lookAt(0, CY, 0);
      if (controlsRef.current) controlsRef.current.enabled = false;
      if (p >= 1) {
        st.endIntro();
        introStartRef.current = null;
        if (controlsRef.current) {
          controlsRef.current.enabled = true;
          controlsRef.current.target.set(0, CY, 0);
        }
      }
      return;
    }
    if (controlsRef.current && !controlsRef.current.enabled) controlsRef.current.enabled = true;

    // --- Active Epicenters "fly to pin" on the globe ---
    if (st.macroFocusNonce !== lastMacroNonceRef.current) {
      lastMacroNonceRef.current = st.macroFocusNonce;
      if (st.macroFocus) macroFlyRef.current = { t0: performance.now(), from: camera.position.clone(), dir: latLonToVector3(st.macroFocus.lat, st.macroFocus.lon, 1).normalize() };
    }
    if (macroFlyRef.current) {
      const f = macroFlyRef.current;
      const p = Math.min((performance.now() - f.t0) / 1500, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      const center = new THREE.Vector3(0, CY, 0);
      const dest = center.clone().add(f.dir.clone().multiplyScalar(4.2));
      camera.position.lerpVectors(f.from, dest, ease);
      camera.lookAt(center);
      if (controlsRef.current) controlsRef.current.enabled = false;
      if (p >= 1) {
        macroFlyRef.current = null;
        if (controlsRef.current) { controlsRef.current.enabled = true; controlsRef.current.target.copy(center); }
      }
      return;
    }

    if (floatRef.current) {
      let shakeX = 0, shakeY = 0;
      const mag = runningMaxMag(st);
      if (mag > 0) {
        const k = Math.pow(Math.max(0, (mag - 3) / 6.5), 2.4);
        const amp = 0.008 + k * 0.09;
        const tt = state.clock.elapsedTime;
        shakeX = Math.sin(tt * 34) * amp;
        shakeY = Math.cos(tt * 29) * amp * 0.7;
      }
      floatRef.current.position.x = shakeX;
      floatRef.current.position.y = CY + Math.sin(state.clock.elapsedTime * 0.9) * 0.05 + shakeY;
    }
    if (controlsRef.current) {
      const dist = controlsRef.current.object.position.distanceTo(controlsRef.current.target);
      const near = dist < 3.7 && st.events.length > 0;
      if (near !== st.canEnterStreet) setCanEnterStreet(near);
    }

    // day/night lighting (clamped so night mode never overexposes)
    const { dir, dayFactor } = sunParams(st.timeOfDay);
    if (ambientRef.current) ambientRef.current.intensity = 0.22 + dayFactor * 0.5;
    if (sunRef.current) {
      sunRef.current.intensity = 0.9 + dayFactor * 2.0;
      sunRef.current.position.set(dir[0], dir[1], dir[2]);
    }
  });

  return (
    <group>
      <ambientLight ref={ambientRef} intensity={0.5} />
      <directionalLight ref={sunRef} position={[5, 3, 5]} intensity={2.6} color="#ffffff" castShadow />
      <directionalLight position={[-6, -2, -4]} intensity={0.6} color="#5a6ba0" />
      <hemisphereLight args={["#3a5688", "#0a0c12", 0.7]} />

      <group ref={floatRef} position={[0, CY, 0]}>
        <Globe />
        <PopulationClusters />
      </group>

      <OrbitControls
        ref={controlsRef}
        makeDefault
        enablePan={false}
        enableRotate={!locked}
        autoRotate={!simulating && !locked}
        autoRotateSpeed={0.35}
        minDistance={3}
        maxDistance={14}
        rotateSpeed={0.6}
        zoomSpeed={0.8}
      />
    </group>
  );
}
